import { Map } from 'immutable';
import { delay } from 'redux-saga';
import { all, call, fork, put, select, take, takeEvery } from 'redux-saga/effects';
import { load as _exif } from 'exifreader/dist/exif-reader';

import { onEvent } from 'helpers/saga';
import request, { uploadUrl } from 'helpers/api';
import { getFlipFromOrientation, getRotationFromOrientation } from 'helpers/photo';
import { dateToExifDate } from 'helpers/time';

import { getActiveOrderId } from 'redux/selectors/app';
import { isAuthenticated } from 'redux/selectors/user';
import { getOrder } from 'redux/selectors/orders';
import { getPhotos, getUploads } from 'redux/selectors/photos';

import { ADD, ADD_EXIF_TEST, setAll, setUpload, UPDATE_PREVIEW } from 'redux/reducers/photos';
import { PICK_MOMENT } from 'redux/reducers/orders';
import { setDraggingPreview, setNotification, setPreview, setUIState } from 'redux/reducers/app';
import { trackEvent } from 'redux/reducers/user';

const EXIF_SAVE_KEYS = ['DateTime', 'Orientation'];
const PROCESS_AT_ONCE = 5;
const PREVIEW_SIZE = 256;
const RESIZE_STEP = 0.5;

const orientation = {
  support: false,
};

(() => {
  // black 2x1 JPEG, with the following meta information set:
  // EXIF Orientation: 6 (Rotated 90° CCW)
  const testImageURL =
    'data:image/jpeg;base64,/9j/4QAiRXhpZgAATU0AKgAAAAgAAQESAAMAAAABAAYAAAA' +
    'AAAD/2wCEAAEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBA' +
    'QEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQE' +
    'BAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAf/AABEIAAEAAgMBEQACEQEDEQH/x' +
    'ABKAAEAAAAAAAAAAAAAAAAAAAALEAEAAAAAAAAAAAAAAAAAAAAAAQEAAAAAAAAAAAAAAAA' +
    'AAAAAEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwA/8H//2Q==';
  var img = document.createElement('img');
  img.onload = function() {
    orientation.support = img.width === 1 && img.height === 2;
  };
  img.src = testImageURL;
})();

const getExifHeight = exif => {
  try {
    if (!exif.PixelYDimension) {
      throw 'PixelYDimension not found';
    }

    return getExifValue(exif.PixelYDimension);
  } catch (e) {
    if (!exif.ImageLength) {
      throw 'ImageLength not found';
    }

    return getExifValue(exif.ImageLength);
  }
};

const getExifWidth = exif => {
  try {
    if (!exif.PixelXDimension) {
      throw 'PixelXDimension not found';
    }

    return getExifValue(exif.PixelXDimension);
  } catch (e) {
    if (!exif.ImageWidth) {
      throw 'ImageWidth not found';
    }

    return getExifValue(exif.ImageWidth);
  }
};

const getExifValue = (exif, fallback = 'unknown') => (exif ? exif.description || exif.value || fallback : fallback);
const createSimpleId = file => `${file.name}-${file.type}-${file.lastModified}-${file.size}`;
const createExifId = (exif, file) =>
  exif.DateTime
    ? `${getExifValue(exif.Make, 'make')}-${getExifValue(exif.Model, 'model')}-${getExifValue(
        exif.DateTime,
        'datetime'
      )}-${file.name}-${file.size}`
    : null;

/**
 * toBlob Promise, so we can use it as generator
 * @param canvas
 * @returns {Promise}
 */
function canvasToBlob(canvas) {
  return new Promise(resolve => {
    canvas.toBlob(blob => {
      resolve(blob);
    });
  });
}

/**
 * Get exif data from jpg
 * @param file
 * @returns {Promise}
 */
function* loadExif(file) {
  const reader = new FileReader();

  const onLoad = yield call(onEvent, reader, 'load', () => {
    // Only read first 128KB of the file for EXIF
    reader.readAsArrayBuffer(file);
  });
  const event = yield take(onLoad);
  const exif = {};

  try {
    const allTags = _exif(event.target.result);

    EXIF_SAVE_KEYS.forEach(tag => {
      if (allTags[tag]) {
        exif[tag] = getExifValue(allTags[tag]);
      }
    });

    // Use file date if no exif is found
    exif.lastModified = dateToExifDate(file.lastModified);

    // Check if exif data contains width and height
    let removeOrientation = false;
    try {
      // If rotation exif isn't there or it's faulty, throw error to avoid storing bad data.
      if (!allTags.Orientation || allTags.Orientation.description === 'Undefined') {
        throw new Error('Orientation is not set.');
      } else {
        exif.width = getExifWidth(allTags);
        exif.height = getExifHeight(allTags);
        exif.ratio = exif.height / exif.width;
      }
    } catch (e) {
      console.error(e, 'No dimensions found');
      removeOrientation = true;
    }

    const { preview, ...dimensions } = yield call(createPreview, file, true, exif);

    if (removeOrientation && exif.Orientation) {
      delete exif.Orientation;
    }

    return {
      file,
      preview,
      exif: {
        ...exif,
        ...dimensions,
      },
      exifId: createExifId(allTags, file),
    };
  } catch (e) {
    if (['No Exif data', 'string is not iterable'].indexOf(e.message) > -1) {
      console.warn('No EXIF data for', file.name);
    } else {
      if (['Invalid image format'].indexOf(e.message) === -1) {
        pe(e);
      }
      console.warn('Error for', file.name, exif, e);
    }

    // const dimensions = yield call(getImageDimensions, file);
    const { preview, ...dimensions } = yield call(createPreview, file, true, exif);

    return {
      file,
      preview,
      exif: {
        ...exif,
        ...(dimensions || {}),
      },
      exifId: null,
    };
  }
}

export function* getExif({ photos }) {
  try {
    // Remove duplicates by simple id
    const orderId = yield select(state => getActiveOrderId(state));
    const existingPhotos = yield select(state => getPhotos(state));
    const existingSimpleIds = existingPhotos.map(photo => photo.get('simpleId'));
    let cleanedPhotos = photos.filter(photo => existingSimpleIds.indexOf(createSimpleId(photo)) === -1);

    let mergedPhotos = [];
    const notificationKey = 'exif' + Math.random();

    while (cleanedPhotos.length > 0) {
      // Notification
      yield put(
        setNotification(
          notificationKey,
          kroostApp.strings.mergingPhotos.replace('{mergedPhotos}', mergedPhotos.length, '{photos}', photos.length)
        )
      );

      const photoChunk = cleanedPhotos.splice(0, PROCESS_AT_ONCE);

      // Get exif for all new photos
      const exifs = yield all(photoChunk.map(photo => call(loadExif, photo)));

      // Find current photos and merge them with the new exif data
      const newPhotos = exifs.map(photo =>
        Map({
          ...photo,
          simpleId: createSimpleId(photo.file),
        })
      );

      mergedPhotos.push(...newPhotos);
    }

    // Get current photos and remove duplicats
    const currentPhotos = yield select(state => getPhotos(state));
    const existingExifIds = currentPhotos.map(photo => photo.get('exifId'));

    mergedPhotos = mergedPhotos.filter(
      photo => photo.get('exifId') === null || existingExifIds.indexOf(photo.get('exifId')) === -1
    );
    const notUsed = photos.length - mergedPhotos.length;

    // Notification
    const doneNotification =
      kroostApp.strings.doneNotificationMessage.replace('{mergedPhotos}', mergedPhotos.length) +
      (notUsed > 0 ? kroostApp.strings.notUsedPhotosMessage.replace('{notused}', notUsed) : '');
    yield put(setNotification(notificationKey, doneNotification));

    yield put(
      trackEvent('photos_imported', {
        no_of_photos: photos.length,
        no_of_photos_ignored: photos.length - mergedPhotos.length,
      })
    );

    // Add new photos
    yield put(setAll(orderId, mergedPhotos));

    // Clear notification
    yield delay(3000);
    yield put(setNotification(notificationKey, null));
  } catch (e) {
    pe(e, 'failed getExif');
  }
}

function* createPreview(file, initial, exif, resizeWidth = PREVIEW_SIZE, userRotate = null) {
  try {
    const isFile = file instanceof File;

    const img = new Image();
    img.setAttribute('crossOrigin', 'anonymous');

    const onLoad = yield call(onEvent, img, 'load', () => {
      img.src = isFile ? URL.createObjectURL(file) : file;
    });

    yield take(onLoad);

    const canvas = document.createElement('canvas'),
      ctx = canvas.getContext('2d');

    const nativeRotateAndFlip = orientation.support && isFile;

    const originalRotate = getRotationFromOrientation(exif.Orientation);
    const originalFlip = getFlipFromOrientation(exif.Orientation);

    // When still a file, and there is no rotation set in exif, use the width and height of what the browsers gives back
    // This fixes issue #129
    const dimensions = [img.width, img.height];
    const hasRatioChange = [0, 180, -180].indexOf(originalRotate) === -1;
    const widthIndex = !hasRatioChange ? 0 : 1;
    const heightIndex = widthIndex === 0 ? 1 : 0;

    const widthMismatch = isFile && dimensions[widthIndex] !== exif.width;
    const heightMisMatch = isFile && dimensions[heightIndex] !== exif.height;

    const exifRatio = exif.width / exif.height;
    const imgRatio = img.width / img.height;
    const differentDimensions =
      isFile &&
      initial &&
      ([exif.width, exif.height].indexOf(img.width) === -1 || [exif.width, exif.height].indexOf(img.height) === -1);
    const ratioChanged = differentDimensions || exifRatio > 1 !== imgRatio > 1;

    // Ignore rotation when file doesn't match exif data
    let ignoreRotation = false;
    if (widthMismatch || heightMisMatch) {
      ignoreRotation = true;
    }

    const fallbackWidth = !ignoreRotation ? exif.width : img.width;
    const fallbackHeight = !ignoreRotation ? exif.height : img.height;

    let originalWidth = (originalRotate === 0 && isFile) || nativeRotateAndFlip ? img.width : fallbackWidth;
    let originalHeight = (originalRotate === 0 && isFile) || nativeRotateAndFlip ? img.height : fallbackHeight;

    // Files are expected to have the correct orientation already
    if (!isFile) {
      switch (originalRotate) {
        case 90:
        case -90:
          originalWidth = exif.height;
          originalHeight = exif.width;
          break;
      }
    }
    const rotate = (userRotate !== null ? userRotate : 0) + (isFile && !nativeRotateAndFlip ? originalRotate : 0);

    let width = originalWidth;
    let height = originalHeight;
    let flip = false;

    switch (rotate) {
      case 90:
      case -90:
      case 270:
        height = originalWidth;
        width = originalHeight;
        flip = true;
        break;
    }

    const ratio = height / width;
    canvas.width = resizeWidth;
    canvas.height = resizeWidth * ratio;

    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotate * Math.PI) / 180);

    // Mirror image
    if (isFile && originalFlip && nativeRotateAndFlip) {
      ctx.scale(-1, 1);
    }

    if (flip) {
      ctx.drawImage(img, -canvas.height / 2, -canvas.width / 2, canvas.height, canvas.width);
    } else {
      ctx.drawImage(img, -canvas.width / 2, -canvas.height / 2, canvas.width, canvas.height);
    }

    const blob = yield call(canvasToBlob, canvas);

    if (isFile) {
      URL.revokeObjectURL(img.src);
    }

    return {
      ratio: height / width,
      rotate,
      flipped: originalFlip,
      preview: URL.createObjectURL(blob),
      ...((ignoreRotation && ratioChanged) || userRotate !== null || !exif.width || !exif.height
        ? {
            height,
            width,
          }
        : {}),
      ...(initial
        ? {
            _height: height,
            _width: width,
          }
        : {}),
    };
  } catch (e) {
    pe(e, 'Failed creating image preview');

    yield put(
      trackEvent('error', {
        type: 'createPreview',
        e: e.toString(),
      })
    );

    return {};
  }
}

export function* updatePreview({ orderId, momentId }) {
  try {
    const uploads = yield select(state => getUploads(state));
    const photos = yield select(state => getPhotos(state));
    const order = yield select(state => getOrder(state));
    const picked = order.getIn(['picked', momentId], null);

    if (picked) {
      const simpleId = picked.get('simpleId');
      const rotate = picked.getIn(['transform', 'rotate'], 0) || null;

      const photo = photos.find(photo => photo.get('simpleId') === simpleId);

      if (photo) {
        let file = photo.get('file');
        const exif = photo.get('exif');

        // File not available, use uploaded image
        if (!file) {
          const uploadUID = uploads.getIn([simpleId, 'uid']);
          file = uploadUrl(orderId, uploadUID, order.get('token'), order.get('user'));
        }

        const existingPreview = yield select(state => state.getIn(['app', 'previews', orderId, momentId]));

        const newPreview = yield call(createPreview, file, false, exif, 768, rotate);

        // Has existing preview?
        if (existingPreview && existingPreview.get('copy') !== true) {
          URL.revokeObjectURL(existingPreview.get('preview'));
        }

        yield put(setDraggingPreview(null));
        yield put(setPreview(orderId, momentId, newPreview));
      }
    }
  } catch (e) {
    pe(e, 'Failed updatePreview');

    yield put(
      trackEvent('error', {
        type: 'updatePreview',
        e: e.toString(),
      })
    );
  }
}

let failCounter = 0;

export function* uploadImage({ simpleId, notifyDelay = 1000 }) {
  try {
    const authenticated = yield select(state => isAuthenticated(state));

    // Check if upload is needed
    const order = yield select(state => getOrder(state));
    const upload = order.getIn(['uploads', simpleId]);

    if (authenticated && !upload) {
      const photo = yield select(state => getPhotos(state).find(photo => photo.get('simpleId') === simpleId));

      if (photo) {
        const fileName = photo.get('file').name;
        yield put(
          setNotification('upload', kroostApp.strings.feedbackUploadingMessage.replace('{fileName}', fileName))
        );

        yield put(
          trackEvent('upload-image-start', {
            fileName,
          })
        );

        const orderId = yield select(state => getActiveOrderId(state));

        try {
          const imageUID = yield call(request, 'media/upload/', {
            auth: true,
            body: {
              file: photo.get('file'),
              order: orderId,
            },
          });

          if (!imageUID) {
            throw new Error('Failed upload');
          }

          yield put(
            setUpload(orderId, photo.get('simpleId'), {
              uid: imageUID,
              exifId: photo.get('exifId'),
              exif: photo.get('exif', {}),
            })
          );

          yield put(setNotification('upload', kroostApp.strings.uploadSuccessMessage.replace('{fileName}', fileName)));
          failCounter = 0;

          yield put(
            trackEvent('upload-image-success', {
              imageUID,
              fileName,
            })
          );
        } catch (e) {
          yield put(
            setNotification('upload', kroostApp.strings.uploandingFaliedMessage.replace('{fileName}', fileName))
          );

          yield put(
            trackEvent('upload-image-failed', {
              fileName,
              e: e.toString(),
            })
          );

          failCounter++;
        }

        if (notifyDelay > 0) {
          yield delay(notifyDelay);
          yield put(setNotification('upload', null));
        }
      }
    }
  } catch (e) {
    pe(e);

    yield put(
      trackEvent('error', {
        type: 'uploadImage',
        e: e.toString(),
      })
    );
    failCounter++;
  }

  if (failCounter === 3) {
    alert(kroostApp.strings.failedUploadWarning);
    window.onbeforeunload = null;
    window.location.reload();
  }
}

/**
 * Upload any images that are set, but don't have an upload ID yet
 */
export function* uploadQueued() {
  while (true) {
    try {
      const authenticated = yield select(state => isAuthenticated(state));
      const isUploading = yield select(state => state.getIn(['app', 'ui', 'is_uploading'], false));

      if (authenticated && !isUploading) {
        const order = yield select(state => getOrder(state));

        // Only upload when order isn't local
        if (!order.get('local', false)) {
          const picked = order.get('picked', Map());
          const uploads = order.get('uploads', Map());

          const queue = [];
          picked.forEach(pick => {
            if (pick.get('simpleId') && !uploads.get(pick.get('simpleId'))) {
              queue.push(pick.get('simpleId'));
            }
          });

          const needsToProcess = queue.length > 0;
          if (needsToProcess) {
            yield put(setUIState('is_uploading', true));
          }

          while (queue.length > 0) {
            const simpleId = queue.shift();
            yield call(uploadImage, { simpleId, notifyDelay: 0 });
          }

          if (needsToProcess) {
            yield put(setUIState('is_uploading', false));
            yield delay(2000);
            yield put(setNotification('upload', null));
          }
        }
      }

      yield delay(1000);
    } catch (e) {
      pe(e);

      yield put(
        trackEvent('error', {
          type: 'uploadQueued',
          e: e.toString(),
        })
      );
    }
  }
}

function* urlAsFile(name, url) {
  const req = new XMLHttpRequest();

  const onLoad = yield call(onEvent, req, 'load', () => {
    req.open('GET', url);
    req.responseType = 'arraybuffer';
    req.send();
  });

  yield take(onLoad);

  const b = new Blob([req.response]);
  return new File([b], name);
}

const uniqueIds = {};

function* getExifTest({ items }) {
  try {
    while (items.length > 0) {
      const { name, url, uploadUrl } = items.shift();

      const fileBlob = yield call(urlAsFile, name, url);
      const photo = yield call(loadExif, fileBlob);

      const uniqueId = `${photo.exif.width}-${photo.exif.height}-${photo.exif.Orientation}-${photo.exif.rotate}-${photo.exif.flipped}`;

      // Don't test the same types of exif images
      if (!(uniqueId in uniqueIds)) {
        uniqueIds[uniqueId] = name;
      } else {
        p('Already a test for this file: ', name, uniqueIds[uniqueId]);
        continue;
      }

      const def = yield call(createPreview, fileBlob, false, photo.exif, 200, 0);
      const rotated = yield call(createPreview, fileBlob, false, photo.exif, 200, 90);

      const uploaded = yield call(createPreview, uploadUrl, false, photo.exif, 200, 0);
      const uploadedRotated = yield call(createPreview, uploadUrl, false, photo.exif, 200, 90);

      yield put(
        setAll('EXIF_TEST', [
          Map({
            name,
            url,
            ...photo,
            simpleId: createSimpleId(photo.file),
            test: {
              default: def,
              rotated,
              uploaded,
              uploadedRotated,
            },
          }),
        ])
      );
    }
  } catch (e) {
    pe(e, 'Failed getExifTest');
  }
}

export default function* saga() {
  yield takeEvery(ADD, getExif);
  yield takeEvery(UPDATE_PREVIEW, updatePreview);
  yield takeEvery(PICK_MOMENT, updatePreview);

  yield takeEvery(ADD_EXIF_TEST, getExifTest);

  yield fork(uploadQueued);
}



// WEBPACK FOOTER //
// ./app/redux/sagas/photos.js